/**
 * SmartDiscoverFeed — Google-Discover-style recommendation carousel.
 *
 * A query-free feed of cards personalized to this landlord's own
 * data — no search, no click, no LLM. Rule-based ranking blends four
 * signal channels:
 *
 *   1. Portfolio signals   — vacant units, expiring leases, late rent,
 *                            stale maintenance tickets, dormant
 *                            applicants (from the dashboard payload).
 *   2. Editorial signals   — 2-3 most-recent Central Rentals blog
 *                            posts tagged to the landlord's own
 *                            province cluster.
 *   3. Market signals      — the landlord's own market-rent scan if
 *                            available (revenue-optimizer feed).
 *   4. Onboarding signals  — one card only if the landlord hasn't
 *                            completed a first-run step yet.
 *
 * Never fetches more than what's already on-screen. Never uses an LLM.
 * "Smart / Copilot / Auto" wording — no "AI" per PRD.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, ArrowRight, TrendingUp, Home, FileText,
  Wrench, ClipboardList, BookOpen, DollarSign,
} from "lucide-react";
import { api } from "@/lib/api";
import "./SmartAccent.css";

// Score every candidate card. Cards with score 0 are filtered.
function rank(cards) {
  return cards
    .filter((c) => c && c.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);
}

function portfolioCards(data, tickets, applications, properties) {
  if (!data) return [];
  const out = [];
  const vacant = Math.max(0, (data.units_total ?? 0) - (data.units_occupied ?? 0));
  if (vacant > 0) out.push({
    icon: Home,
    tone: "urgent",
    eyebrow: "Vacant units",
    title: `${vacant} unit${vacant === 1 ? "" : "s"} sitting empty`,
    body: `Copilot can auto-list and screen applicants for you. Empty doors cost you rent every day.`,
    to: "/properties?filter=vacant",
    cta: "Draft listings",
    score: 100 + vacant * 5,
  });

  const staleTix = (tickets || []).filter((t) => t?.status !== "closed" && (t?.age_days ?? 0) > 5).length;
  if (staleTix > 0) out.push({
    icon: Wrench,
    tone: "urgent",
    eyebrow: "Maintenance",
    title: `${staleTix} ticket${staleTix === 1 ? "" : "s"} open more than 5 days`,
    body: `Copilot can nudge the vendor with the ticket photos attached.`,
    to: "/maintenance",
    cta: "Review tickets",
    score: 90 + staleTix * 3,
  });

  const pendingApps = (applications || []).filter((a) => a?.status === "pending" || a?.status === "new").length;
  if (pendingApps > 0) out.push({
    icon: ClipboardList,
    tone: "info",
    eyebrow: "Applicants waiting",
    title: `${pendingApps} application${pendingApps === 1 ? " is" : "s are"} waiting on you`,
    body: "Every day you delay, they apply somewhere else. Screen in 90 seconds.",
    to: "/applications",
    cta: "Screen now",
    score: 80 + pendingApps * 2,
  });

  const expiring = data.leases_expiring_14d ?? 0;
  if (expiring > 0) out.push({
    icon: FileText,
    tone: "info",
    eyebrow: "Renewals",
    title: `${expiring} lease${expiring === 1 ? "" : "s"} expiring in 14 days`,
    body: "Copilot drafts a renewal offer for each with the current market rent.",
    to: "/renewals",
    cta: "Draft renewals",
    score: 75 + expiring * 4,
  });

  const late = data.tenants_5d_late ?? 0;
  if (late > 0) out.push({
    icon: DollarSign,
    tone: "urgent",
    eyebrow: "Late rent",
    title: `${late} tenant${late === 1 ? " is" : "s are"} 5+ days behind`,
    body: "Copilot can send a friendly reminder that keeps the rent-on-time score alive.",
    to: "/rent?filter=late",
    cta: "Send reminders",
    score: 95 + late * 3,
  });

  // Onboarding — only if they have zero properties.
  if ((properties?.length ?? 0) === 0) out.push({
    icon: Home,
    tone: "onboarding",
    eyebrow: "Get started",
    title: "Add your first property — 30 seconds",
    body: "Central Rentals is a lot more useful once there's a door in it. Address, photos, rent. Done.",
    to: "/properties/new",
    cta: "Add property",
    score: 200,
  });

  return out;
}

function marketCard() {
  return {
    icon: TrendingUp,
    tone: "info",
    eyebrow: "Market rent",
    title: "Copilot can scan your portfolio for missed rent gains",
    body: "Deterministic scan against CMHC + market-listing data. No LLM cost. See what each unit is under-priced by.",
    to: "/revenue-optimizer",
    cta: "Run market scan",
    score: 40,
  };
}

export default function SmartDiscoverFeed({ data, tickets, applications, properties }) {
  const [blogCards, setBlogCards] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/blog/posts?limit=6");
        if (cancelled) return;
        const posts = Array.isArray(r.data) ? r.data : (r.data?.posts || []);
        // Dedupe by base title — the auto-blog generator sometimes emits
        // twin posts whose titles differ only by a trailing timestamp
        // ("Jul 29 2026 10:55 update" vs "Jul 29, 2026 update"), which
        // rendered the same headline twice in a row on the dashboard.
        const seen = new Set();
        const uniq = [];
        for (const p of posts) {
          const key = (p.title || "")
            .toLowerCase()
            .replace(/[—\-–]\s*\w{3,9}\s+\d{1,2},?\s*\d{4}.*$/, "")
            .replace(/[^a-z0-9]+/g, " ")
            .trim();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          uniq.push(p);
          if (uniq.length >= 3) break;
        }
        setBlogCards(uniq.map((p) => ({
          icon: BookOpen,
          tone: "editorial",
          eyebrow: p.category || "Fresh insight",
          title: p.title,
          body: (p.excerpt || "").slice(0, 140),
          to: `/blog/${p.slug}`,
          cta: `${p.read_mins ?? 4} min read`,
          score: 30,
        })));
      } catch { /* silent — blog is optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const cards = rank([
    ...portfolioCards(data, tickets, applications, properties),
    ...blogCards,
    marketCard(),
  ]);

  if (cards.length === 0) return null;

  return (
    <section data-testid="smart-discover-feed" className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700">
          <Sparkles className="w-3 h-3" /> For you
        </div>
        <div className="text-[10px] text-slate-500">Personalized · updated live</div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c, i) => {
          const Icon = c.icon;
          const toneRing =
            c.tone === "urgent" ? "border-rose-200 bg-rose-50/40" :
            c.tone === "onboarding" ? "border-emerald-200 bg-emerald-50/40" :
            c.tone === "editorial" ? "border-slate-200 bg-white" :
            "border-sky-200 bg-sky-50/40";
          return (
            <Link
              key={i}
              to={c.to}
              data-testid={`smart-discover-card-${i}`}
              className={`group block p-4 rounded-2xl border ${toneRing} hover:shadow-md transition-all`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white border border-slate-200 flex-none">
                  <Icon className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">{c.eyebrow}</div>
                  <div className="font-display text-sm font-extrabold text-slate-900 leading-snug">{c.title}</div>
                  {c.body && <p className="mt-1.5 text-xs text-slate-600 leading-relaxed line-clamp-3">{c.body}</p>}
                  <div className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 group-hover:text-emerald-800">
                    {c.cta} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
